"use client";

import { useEffect } from "react";

const SESSION_KEY = "openings-pv-tracked";

interface PageViewTrackerProps {
  salonId: string;
}

/**
 * Fires a single page-view beacon per browser session to the API.
 * Placed in the web root layout for tenant pages.
 */
export default function PageViewTracker({ salonId }: PageViewTrackerProps) {
  useEffect(() => {
    if (!salonId) return;

    // Debounce: only fire once per session
    try {
      const tracked = sessionStorage.getItem(SESSION_KEY);
      if (tracked === salonId) return;
      sessionStorage.setItem(SESSION_KEY, salonId);
    } catch {
      // sessionStorage unavailable (e.g. incognito in some browsers) — continue
    }

    const apiBase = (
      process.env.NEXT_PUBLIC_API_BASE ?? ""
    ).replace(/\/$/, "");
    if (!apiBase) return;

    const url = `${apiBase}/v1/analytics/pageview`;
    const body = JSON.stringify({ salonId });

    // Prefer sendBeacon for reliability (survives page unload)
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
    } else {
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {
        // Analytics tracking is best-effort — swallow errors
      });
    }
  }, [salonId]);

  return null;
}
